/**
 * Config Loader Hook
 *
 * Fetches app configuration from backend on mount.
 * Should be called once in the App component.
 */

import { useEffect } from 'react';
import { useConfigStore } from '../stores/config';
import { configApi } from '../api/endpoints/config';
import { checkpoint, createLogger } from '@/utils/logger';

const logger = createLogger('ConfigLoader');

export function useConfigLoader() {
  const { setConfig, setConfigError, isConfigLoaded, configError } = useConfigStore();

  useEffect(() => {
    // Fetch only when neither resolved state is set. A recorded configError
    // is a terminal state here — the retry button calls resetConfig(), which
    // clears both isConfigLoaded and configError and re-fires this effect.
    if (isConfigLoaded || configError) {
      logger.debug('Config already resolved', { isConfigLoaded, hasError: !!configError });
      return;
    }

    const loadConfig = async () => {
      logger.debug( 'Starting config fetch...');
      const startTime = performance.now();

      try {
        const config = await configApi.getConfig();
        const duration = Math.round(performance.now() - startTime);

        logger.debug( 'Config fetched', {
          version: config.version,
          durationMs: duration,
          profileFields: config.profile?.fields?.length,
        });

        setConfig(config);
        checkpoint(`Config loaded from backend v${config.version}`);
      } catch (error) {
        const duration = Math.round(performance.now() - startTime);
        const err = error instanceof Error ? error : new Error(String(error));
        // No fallback config: the failure is recorded as-is. App.tsx's route
        // guards render a visible error+retry state (ConfigErrorScreen) from
        // this — never a substitute config that reads as a real one.
        logger.error( `Failed to load config after ${duration}ms`, err);
        setConfigError(err);
      }
    };

    void loadConfig();
  }, [isConfigLoaded, configError, setConfig, setConfigError]);
}
